/**
 * External approval (Freigabegrenze CAPEX) UI for invoice page.
 * - Third approval switch (Externe Freigabe) - greyed out, not editable by employees
 * - Badge "Externe Freigabe erforderlich"
 * - Button "Externe Freigabe einholen" and dialog to request external approval
 * - Optional skip (when enabled on property): dialog with Begründung + preset reasons, yellow switch
 */

// Placeholders: {{anredeSuffix}} ("" or "r" for geehrte/r), {{anrede}}, {{name}}, {{legalCompanyName}}
const DEFAULT_EMAIL_BODY =
  'Sehr geehrte{{anredeSuffix}} {{anrede}} {{name}},\n\ndie folgende Rechnung übersteigt die vereinbarte Freigabegrenze, aus diesem Grund würden wir Sie bitten den Freigabeprozess über die Schaltfläche unten zu starten. Die zugrundeliegende Rechnung können Sie auch dort einsehen.\n\nMit freundlichen Grüßen\nIhr Team der {{legalCompanyName}}'

/** Font stack matching kunden portal (Fustat, IBM Plex Sans, etc.) for dialog consistency */
const DIALOG_FONT_FAMILY = "'Fustat', 'IBM Plex Sans', 'Arial', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', 'Fira Sans', 'Oxygen', 'Segoe UI', 'Roboto', 'Ubuntu', 'Cantarell', 'Droid Sans', sans-serif"

function getDefaultBodyWithCompanyName(companyName) {
  const name = companyName || '{{legalCompanyName}}'
  return DEFAULT_EMAIL_BODY.replace(/\{\{legalCompanyName\}\}/g, name)
}

// Map API salutation (MR, MRS, etc.) and optional title to German anrede; return { anrede, isMale } for greeting
function salutationToGerman(salutation, title) {
  var s = (salutation || '').toString().toUpperCase().trim()
  var t = (title || '').toString().trim()
  var isMale = true
  var base = 'Herr'
  if (s === 'MR' || s === 'HERR') {
    base = 'Herr'
    isMale = true
  } else if (s === 'MRS' || s === 'MS' || s === 'MISS' || s === 'FRAU') {
    base = 'Frau'
    isMale = false
  } else if (s === 'DR') {
    base = 'Herr Dr.'
    isMale = true
  } else if (s) {
    base = 'Herr'
    isMale = true
  }
  if (t) {
    if ((base === 'Herr Dr.' || base === 'Herr') && (t.indexOf('Prof') !== -1 || t.indexOf('Dr') !== -1)) {
      base = 'Herr ' + t
    } else if (base === 'Frau' && (t.indexOf('Dr') !== -1 || t.indexOf('Prof') !== -1)) {
      base = 'Frau ' + t
    } else if (base === 'Herr' && t) {
      base = 'Herr ' + t
    } else if (base === 'Frau' && t) {
      base = 'Frau ' + t
    }
  }
  return { anrede: base, isMale: isMale }
}

// Build name and salutation from API contact (PERSON vs COMPANY/MANAGEMENT_COMPANY)
function contactNameAndSalutation(contact) {
  if (!contact) return { name: 'Unbekannt', anrede: 'Herr', isMale: true, isCompany: false }
  var type = (contact.type || '').toString().toUpperCase()
  var isCompany = type !== 'PERSON'
  if (isCompany) {
    var name = (contact.companyName && contact.companyName.trim()) || contact.displayName || contact.name || 'Unbekannt'
    return { name: name || 'Unbekannt', anrede: 'Damen und Herren', isMale: false, isCompany: true }
  }
  var personName = [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.displayName || contact.name || 'Unbekannt'
  var sal = salutationToGerman(contact.salutation, contact.title)
  return { name: personName || 'Unbekannt', anrede: sal.anrede, isMale: sal.isMale, isCompany: false }
}

let approvalData = null

function formatApprovalDateTime(isoString) {
  if (!isoString) return ''
  try {
    const d = new Date(isoString)
    return d.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })
  } catch {
    return ''
  }
}

function openApprovalMetaText(approval) {
  var dt = formatApprovalDateTime(approval && (approval.changedAt || approval.createdAt))
  var prefix = (approval && approval.emailContent) ? '— gesendet' : '— angefragt'
  return dt ? prefix + ' · ' + dt : prefix
}

// (Optional) inject spinner keyframes once
function ensureApprovalCardStyles() {
  if (document.getElementById('approval-card-styles')) return
  const style = document.createElement('style')
  style.id = 'approval-card-styles'
  style.textContent = `
    @keyframes approvalSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    .approval-spinner { animation: approvalSpin 0.8s linear infinite; }
    @keyframes approvalSkeletonShimmer {
      0% { opacity: 0.4; }
      50% { opacity: 0.8; }
      100% { opacity: 0.4; }
    }
    .external-approval-approver-skeleton {
      display: inline-block;
      height: 10px;
      width: 80px;
      max-width: 100%;
      background: linear-gradient(90deg, #e5e5e5 25%, #f0f0f0 50%, #e5e5e5 75%);
      background-size: 200% 100%;
      animation: approvalSkeletonShimmer 1.2s ease-in-out infinite;
      border-radius: 2px;
      vertical-align: middle;
    }
  `
  document.head.appendChild(style)
}

const SKIP_REASON_HANDWERKER = 'Handwerker Rechnung'
const SKIP_REASON_OTHER = 'Externe Freigabe anderweitig erhalten'

function applyExternalApprovalSkippedVisual(cardApi, setBadgeStatus) {
  if (!cardApi) return
  cardApi.checkbox.checked = true
  cardApi.slider.style.backgroundColor = '#eab308'
  cardApi.sliderDot.style.transform = 'translateX(22px)'
  if (cardApi.toggleWrapper) cardApi.toggleWrapper.title = 'Rechnungsfreigabe übersprungen'
  if (cardApi.actionBtn) cardApi.actionBtn.style.display = 'none'
  if (cardApi.actionsMenu) cardApi.actionsMenu.hide()
  if (cardApi.spinner) cardApi.spinner.style.display = 'none'
  cardApi.metaLabel.textContent = '— übersprungen'
  cardApi.metaLabel.style.display = 'inline'
  if (cardApi.hideApprover) cardApi.hideApprover()
  if (setBadgeStatus) setBadgeStatus('skipped')
}

/** When property no longer allows skip, ignore persisted skip in UI and restore request-by-email flow. */
function restoreExternalApprovalRequestUi(cardApi, setBadgeStatus) {
  if (!cardApi) return
  var status = approvalData && approvalData.status
  if (status === 'approved') return

  cardApi.checkbox.checked = false
  cardApi.slider.style.backgroundColor = status === 'rejected' ? 'red' : '#ccc'
  cardApi.sliderDot.style.transform = 'translateX(0)'
  if (cardApi.toggleWrapper) cardApi.toggleWrapper.title = ''
  if (cardApi.spinner) cardApi.spinner.style.display = 'none'

  var statusDateTime = approvalData
    ? formatApprovalDateTime(approvalData.changedAt || approvalData.createdAt)
    : ''

  if (status === 'rejected') {
    cardApi.metaLabel.textContent = statusDateTime ? '·  ' + statusDateTime : ''
    cardApi.metaLabel.style.display = 'inline'
    if (cardApi.actionBtn) cardApi.actionBtn.style.display = 'inline-flex'
    if (approvalData.approvingUser && cardApi.loadApproverName) {
      cardApi.loadApproverName(approvalData.approvingUser)
    }
    if (setBadgeStatus) setBadgeStatus('rejected')
  } else if (status === 'open') {
    cardApi.metaLabel.textContent = openApprovalMetaText(approvalData)
    cardApi.metaLabel.style.display = 'inline'
    if (cardApi.actionBtn) cardApi.actionBtn.style.display = 'inline-flex'
    if (approvalData.approvingUser && cardApi.loadApproverName) {
      cardApi.loadApproverName(approvalData.approvingUser)
    }
    if (setBadgeStatus) setBadgeStatus('open')
  } else {
    cardApi.metaLabel.style.display = 'none'
    if (cardApi.hideApprover) cardApi.hideApprover()
    if (cardApi.actionBtn) cardApi.actionBtn.style.display = 'inline-flex'
    if (setBadgeStatus) setBadgeStatus(null)
  }
  if (cardApi.refreshSkipMenu) cardApi.refreshSkipMenu()
}

function createApprovalCard(id, title, disabled , openExternalApprovalDialog, setBadgeStatus, cardOptions) {
  cardOptions = cardOptions || {}
  ensureApprovalCardStyles()

  const card = document.createElement('div')
  card.className = 'approval-card external-approval-card'
  card.dataset.approvalId = id
  card.style.cssText = `
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    background-color: #fff;
    border: 1px solid #e5e5e5;
    border-radius: 4px;
    transition: all 0.2s ease;
    gap: 12px;
  `
  if (disabled) {
    card.style.opacity = '0.7'
    card.style.pointerEvents = 'auto'
  }

  // Left section: row 1 = title + meta, row 2 = approver email (small)
  const leftSection = document.createElement('div')
  leftSection.style.cssText = `
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    min-width: 0;
    flex: 1;
    overflow: hidden;
  `

  const titleRow = document.createElement('div')
  titleRow.style.cssText = 'display: flex; align-items: center; gap: 8px; min-width: 0; flex: 1; overflow: hidden;'

  const titleLabel = document.createElement('span')
  titleLabel.textContent = title
  titleLabel.style.cssText = `
    font-size: 14px;
    font-weight: 600;
    color: #1a1a1a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex-shrink: 0;
  `

  const metaLabel = document.createElement('span')
  metaLabel.className = 'external-approval-meta'
  metaLabel.style.cssText = `
    font-size: 11px;
    color: #999;
    display: none;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
    flex: 1;
  `
  titleRow.appendChild(titleLabel)
  titleRow.appendChild(metaLabel)
  leftSection.appendChild(titleRow)

  const approverNameWrapper = document.createElement('span')
  approverNameWrapper.className = 'external-approval-approver-name-wrapper'
  approverNameWrapper.style.cssText = `
    font-size: 10px;
    color: #999;
    display: none;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  `
  const approverNameLabel = document.createElement('span')
  approverNameLabel.className = 'external-approval-approver-name'
  approverNameLabel.style.display = 'none'
  const approverSkeleton = document.createElement('span')
  approverSkeleton.className = 'external-approval-approver-skeleton'
  approverSkeleton.style.display = 'none'
  approverNameWrapper.appendChild(approverSkeleton)
  approverNameWrapper.appendChild(approverNameLabel)

  function showApproverSkeleton() {
    approverSkeleton.style.display = 'inline-block'
    approverNameLabel.textContent = ''
    approverNameLabel.style.display = 'none'
    approverNameWrapper.style.display = 'block'
  }
  function setApproverName(name) {
    approverSkeleton.style.display = 'none'
    approverNameLabel.textContent = name || 'Unbekannt'
    approverNameLabel.style.display = 'inline'
    approverNameWrapper.style.display = 'block'
  }
  function hideApprover() {
    approverNameWrapper.style.display = 'none'
    approverSkeleton.style.display = 'none'
    approverNameLabel.textContent = ''
  }
  function loadApproverName(contactId) {
    if (!contactId) return setApproverName('')
    showApproverSkeleton()
    var authToken = null
    try { authToken = JSON.parse(localStorage.auth).token } catch (e) { return setApproverName('') }
    var origin = window.location.origin
    var url = origin + '/services/pmp-accounting/api/v1/contacts/' + encodeURIComponent(contactId)
    fetch(url, { headers: { 'Authorization': 'Bearer ' + authToken, 'Content-Type': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null })
      .then(function (contact) {
        if (!contact) return setApproverName('')
        var info = contactNameAndSalutation(contact)
        setApproverName(info.name)
      })
      .catch(function () { setApproverName('') })
  }

  leftSection.appendChild(approverNameWrapper)

  // Right container: [Email SVG button] [Slider]   (aligned)
  const rightControls = document.createElement('div')
  rightControls.style.cssText = `
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    flex-shrink: 0;
  `

  // Spinner (shown while fetch pending)
  const spinner = document.createElement('div')
  spinner.className = 'approval-spinner'
  spinner.style.cssText = `
    width: 18px;
    height: 18px;
    border: 2px solid rgba(0,0,0,0.15);
    border-top-color: #1890ff;
    border-radius: 50%;
    display: inline-block;
  `
  rightControls.appendChild(spinner)

  // Email SVG action button (left of slider)
  const actionBtn = document.createElement('button')
  actionBtn.type = 'button'
  actionBtn.setAttribute('aria-label', 'Externe Freigabe einholen')
  actionBtn.title = 'Externe Freigabe einholen'
  actionBtn.style.cssText = `

    display: none;
    align-items: center;
    justify-content: center;
    border: none;

    background: #fff;
    padding: 0;
    line-height: 0;
    cursor: pointer;
  `
  actionBtn.innerHTML = `
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 6.5C4 5.67157 4.67157 5 5.5 5H18.5C19.3284 5 20 5.67157 20 6.5V17.5C20 18.3284 19.3284 19 18.5 19H5.5C4.67157 19 4 18.3284 4 17.5V6.5Z" stroke="#1890ff" stroke-width="1.8"/>
      <path d="M6 7.5L12 12.2L18 7.5" stroke="#1890ff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `
  actionBtn.onclick = () => openExternalApprovalDialog(metaLabel, { setApproverName: setApproverName, showApprover: function () { approverNameWrapper.style.display = 'block' } }, titleLabel, title, actionBtn)
  rightControls.appendChild(actionBtn)

  // Toggle (slider)
  const toggleWrapper = document.createElement('label')
  toggleWrapper.style.cssText = `
    position: relative;
    display: inline-block;
    width: 48px;
    height: 26px;
    flex-shrink: 0;
  `
  if (disabled) toggleWrapper.style.cursor = 'not-allowed'

  const checkbox = document.createElement('input')
  checkbox.type = 'checkbox'
  checkbox.id = id
  checkbox.disabled = !!disabled
  checkbox.style.cssText = `
    opacity: 0;
    width: 0;
    height: 0;
  `

  const slider = document.createElement('span')
  slider.className = 'toggle-slider'
  slider.style.cssText = `
    position: absolute;
    cursor: ${disabled ? 'not-allowed' : 'pointer'};
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    opacity: 0.5;
    transition: 0.3s;
    border-radius: 26px;
  `

  const sliderDot = document.createElement('span')
  sliderDot.className = 'toggle-dot'
  sliderDot.style.cssText = `
    position: absolute;
    content: "";
    height: 20px;
    width: 20px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  `

  slider.appendChild(sliderDot)
  toggleWrapper.appendChild(checkbox)
  toggleWrapper.appendChild(slider)
  rightControls.appendChild(toggleWrapper)

  // Fetch status + spinner handling
  const invoiceId = window.location.pathname.split('/').pop()
  const contractsUrl = baseApiUrl + `/api/invoices/${invoiceId}/capex-request-approval`
  const authToken = JSON.parse(localStorage.auth).token
  const headers = { Authorization: 'Bearer ' + authToken, 'Content-Type': 'application/json' }

  fetch(contractsUrl, { headers })
    .then(r => (r.ok ? r.json() : null))
    .then(data => {
      const status = data?.capexApproval?.status
      const suppressActions = cardOptions.shouldSuppressRequestActions && cardOptions.shouldSuppressRequestActions()
      if(!status) {
        if (!suppressActions) actionBtn.style.display = 'inline-flex'
        return
      }
      approvalData = data.capexApproval
      const approvingUser = approvalData?.approvingUser
      const statusDateTime = formatApprovalDateTime(approvalData?.changedAt || approvalData?.createdAt)
      if (status === 'approved') {
        checkbox.checked = true
        slider.style.backgroundColor = '#22c55e'
        sliderDot.style.transform = 'translateX(22px)'
        metaLabel.textContent = statusDateTime ? '·  ' + statusDateTime : ''
        metaLabel.style.display = 'inline'
        actionBtn.style.display = 'none'
        if (approvingUser) {
          loadApproverName(approvingUser)
        }
      } else if (status === 'rejected') {
        slider.style.backgroundColor = 'red'
        sliderDot.style.transform = 'translateX(0)'
        metaLabel.textContent = statusDateTime ? '·  ' + statusDateTime : ''
        metaLabel.style.display = 'inline'
        actionBtn.style.display = 'inline-flex'
        if (approvingUser) {
          loadApproverName(approvingUser)
        }
      } else if (status === 'open') {
        metaLabel.textContent = openApprovalMetaText(approvalData)
        metaLabel.style.display = 'inline'
        actionBtn.style.display = 'inline-flex'
        if (approvingUser) {
          loadApproverName(approvingUser)
        }
      }
      if (setBadgeStatus && status) setBadgeStatus(status)
      if (cardOptions.onControlsRefresh) cardOptions.onControlsRefresh()
    })
    .catch(() => {
      const suppressActions = cardOptions.shouldSuppressRequestActions && cardOptions.shouldSuppressRequestActions()
      if (!suppressActions) actionBtn.style.display = 'inline-flex'
      if (cardOptions.onControlsRefresh) cardOptions.onControlsRefresh()
    })
    .finally(() => {
      spinner.style.display = 'none'
      if (cardOptions.onControlsRefresh) cardOptions.onControlsRefresh()
    })

  checkbox.addEventListener('change', function () {
    if (this.checked) {
      slider.style.backgroundColor = '#22c55e'
      sliderDot.style.transform = 'translateX(22px)'
    } else {
      slider.style.backgroundColor = '#ccc'
      sliderDot.style.transform = 'translateX(0)'
    }
  })

  card.appendChild(leftSection)
  card.appendChild(rightControls)

  return {
    card,
    checkbox,
    metaLabel,
    approverNameWrapper,
    setApproverName,
    loadApproverName,
    hideApprover,
    slider,
    sliderDot,
    actionBtn,
    spinner,
    rightControls,
    toggleWrapper,
  }
}

function buildSkipExternalApprovalDialog(onSuccess) {
  var invoiceId = window.location.pathname.split('/').pop()
  buildSkipApprovalReasonDialog({
    overlayId: 'external-approval-skip-dialog-overlay',
    title: 'Rechnungsfreigabe überspringen',
    radioGroupName: 'skip-reason',
    presets: [
      { value: 'handwerker', label: SKIP_REASON_HANDWERKER },
      { value: 'anderweitig', label: SKIP_REASON_OTHER },
    ],
    onSuccess: onSuccess,
    submitRequest: function (body) {
      var authToken = JSON.parse(localStorage.auth).token
      return fetch(baseApiUrl + '/api/invoices/' + invoiceId + '/capex-skip-approval', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + authToken, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).then(function (r) {
        return r.json().then(function (j) {
          return { ok: r.ok, j: j }
        })
      })
    },
  })
}

function openExternalApprovalDialog(approvalBadgeLabel, approverLabelApi, titleLabel, baseTitle, actionBtn) {
  const invoiceId = window.location.pathname.split('/').pop()
  const authToken = JSON.parse(localStorage.auth).token
  const headers = { Authorization: 'Bearer ' + authToken, 'Content-Type': 'application/json' }

  const emailIconHtml = actionBtn ? actionBtn.innerHTML : null
  if (actionBtn) {
    actionBtn.disabled = true
    actionBtn.style.pointerEvents = 'none'
    actionBtn.style.opacity = '0.6'
    actionBtn.innerHTML = `
      <div class="approval-spinner" style="width:18px;height:18px;border:2px solid rgba(0,0,0,0.15);border-top-color:#1890ff;border-radius:50%;"></div>
    `
  }

  fetch(baseApiUrl + `/api/invoices/${invoiceId}/capex-request-approval`, { headers })
    .then(r => (r.ok ? r.json() : null))
    .then(data => {
      approvalData = (data && data.capexApproval) || null
      var approverContact = null
      if (approvalData && approvalData.approvingUser) {
        var contactId = approvalData.approvingUser
        var origin = typeof window !== 'undefined' ? window.location.origin : ''
        var contactsUrl = origin + '/services/pmp-accounting/api/v1/contacts/' + encodeURIComponent(contactId)
        return fetch(contactsUrl, { headers: { 'Authorization': 'Bearer ' + authToken, 'Content-Type': 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : null })
          .then(function (contact) {
            if (!contact) return buildExternalApprovalDialog(approvalBadgeLabel, approverLabelApi, titleLabel, baseTitle, null)
            var info = contactNameAndSalutation(contact)
            var contactEmail = contact.email
            if (contactEmail != null && typeof contactEmail === 'object' && !Array.isArray(contactEmail)) contactEmail = contactEmail.value
            else if (Array.isArray(contactEmail)) contactEmail = contactEmail[0]
            if (contactEmail != null && typeof contactEmail === 'object' && contactEmail.value) contactEmail = contactEmail.value
            else if (typeof contactEmail !== 'string') contactEmail = ''
            contactEmail = (contactEmail || '').trim() || (approvalData.approverEmail || '')
            approverContact = { id: contactId, name: info.name, email: contactEmail, anrede: info.anrede, isMale: info.isMale, isCompany: info.isCompany, label: info.name + (contactEmail ? ' · ' + contactEmail : '') }
            buildExternalApprovalDialog(approvalBadgeLabel, approverLabelApi, titleLabel, baseTitle, approverContact)
          })
          .catch(function () { buildExternalApprovalDialog(approvalBadgeLabel, approverLabelApi, titleLabel, baseTitle, null) })
      }
      buildExternalApprovalDialog(approvalBadgeLabel, approverLabelApi, titleLabel, baseTitle, approverContact)
    })
    .catch(() => { approvalData = null; buildExternalApprovalDialog(approvalBadgeLabel, approverLabelApi, titleLabel, baseTitle, null) })
    .finally(() => {
      if (actionBtn && emailIconHtml != null) {
        actionBtn.innerHTML = emailIconHtml
        actionBtn.style.pointerEvents = ''
        actionBtn.style.opacity = ''
        actionBtn.disabled = false
      }
    })
}

function buildExternalApprovalDialog(approvalBadgeLabel, approverLabelApi, titleLabel, baseTitle, approverContactFromApi) {
  const overlay = document.createElement('div')
  overlay.id = 'external-approval-dialog-overlay'
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    font-family: ${DIALOG_FONT_FAMILY};
  `

  const dialog = document.createElement('div')
  dialog.style.cssText = `
    background: hsl(0, 0%, 100%);
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.12);
    width: 90%;
    max-width: 560px;
    max-height: 90vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    font-family: ${DIALOG_FONT_FAMILY};
  `

  const header = document.createElement('div')
  header.style.cssText = `
    padding: 16px 20px;
    border-bottom: 1px solid hsl(214.3, 31.8%, 91.4%);
    font-size: 18px;
    font-weight: 600;
    color: hsl(222.2, 84%, 4.9%);
    font-family: ${DIALOG_FONT_FAMILY};
  `
  header.textContent = 'Externe Freigabe einholen'
  if(approvalData) {
    header.textContent = 'Externe Freigabe erneut einholen'
  }
  const body = document.createElement('div')
  body.style.cssText = `
    padding: 20px;
    overflow-y: auto;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 16px;
    font-family: ${DIALOG_FONT_FAMILY};
  `

  // Contact search – caseComboBox-style: recommended by property type + search any contact
  const contactLabel = document.createElement('label')
  contactLabel.style.cssText = `font-size: 14px; font-weight: 500; color: hsl(222.2, 84%, 4.9%); display: block; margin-bottom: 6px; font-family: ${DIALOG_FONT_FAMILY};`
  contactLabel.textContent = 'Kontakt suchen'

  const contactRoot = document.createElement('div')
  contactRoot.style.cssText = 'position: relative; width: 100%;'

  const contactDisplay = document.createElement('input')
  contactDisplay.type = 'text'
  contactDisplay.className = 'ant-input'
  contactDisplay.placeholder = 'Bitte auswählen oder eintippen...'
  contactDisplay.style.cssText = `
    width: 100%;
    border-radius: 4px;
    padding: 8px 36px 8px 12px;
    font-size: 14px;
    cursor: pointer;
    box-shadow: none;
    border: 1px solid hsl(214.3, 31.8%, 91.4%);
    box-sizing: border-box;
    font-family: ${DIALOG_FONT_FAMILY};
  `
  contactRoot.addEventListener('mouseover', function () {
    if (!contactDisplay.disabled) {
      contactDisplay.style.boxShadow = '0 0 0 2px hsl(214.3, 31.8%, 91.4%)'
      contactDisplay.style.borderColor = 'hsl(214.3, 31.8%, 91.4%)'
      contactDisplay.style.outline = 'none'
    }
  })
  contactRoot.addEventListener('mouseout', function () {
    contactDisplay.style.boxShadow = 'none'
    contactDisplay.style.borderColor = 'hsl(214.3, 31.8%, 91.4%)'
  })

  const contactDrop = document.createElement('div')
  contactDrop.style.cssText = `
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    max-height: 200px;
    overflow-y: auto;
    background: hsl(0, 0%, 100%);
    border: 1px solid hsl(214.3, 31.8%, 91.4%);
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
    display: none;
    z-index: 9999;
    margin-top: 4px;
    font-family: ${DIALOG_FONT_FAMILY};
  `
  const contactOpts = document.createElement('div')
  const contactSpinner = document.createElement('div')
  contactSpinner.style.cssText = `display: none; padding: 12px; text-align: center; font-size: 14px; color: hsl(215.4, 16.3%, 46.9%); font-family: ${DIALOG_FONT_FAMILY};`
  contactSpinner.textContent = 'Laden…'
  contactDrop.appendChild(contactSpinner)
  contactDrop.appendChild(contactOpts)

  const contactClearBtn = document.createElement('button')
  contactClearBtn.type = 'button'
  contactClearBtn.innerHTML = '×'
  contactClearBtn.setAttribute('aria-label', 'Auswahl löschen')
  contactClearBtn.style.cssText = `
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    cursor: pointer;
    font-size: 20px;
    color: hsl(215.4, 16.3%, 46.9%);
    width: 24px;
    height: 24px;
    display: none;
    line-height: 1;
    padding: 0;
    border-radius: 2px;
    transition: color 0.2s, background 0.2s;
    font-family: ${DIALOG_FONT_FAMILY};
  `
  contactClearBtn.addEventListener('mouseover', function () {
    contactClearBtn.style.color = '#ff4d4f'
    contactClearBtn.style.background = '#fff2f0'
  })
  contactClearBtn.addEventListener('mouseout', function () {
    contactClearBtn.style.color = 'hsl(215.4, 16.3%, 46.9%)'
    contactClearBtn.style.background = 'transparent'
  })
  contactClearBtn.addEventListener('click', function (e) {
    e.stopPropagation()
    selectedContact = null
    contactDisplay.value = ''
    contactDisplay.placeholder = 'Bitte auswählen oder eintippen...'
    contactClearBtn.style.display = 'none'
    emailWarning.style.display = 'none'
    contactDrop.style.display = 'block'
    renderContactOptions(contacts)
    contactDisplay.focus()
  })

  var contacts = []
  let selectedContact = null
  var defaultBodyWithCompany = null
  var contactSearchTimeout = null
  var sendEmailMode = true

  function setContactInputLoading(loading) {
    contactDisplay.disabled = !!loading
    contactDisplay.placeholder = loading ? 'Laden…' : 'Bitte auswählen oder eintippen...'
    contactDisplay.style.backgroundColor = loading ? 'hsl(210, 40%, 96.1%)' : ''
    contactDisplay.style.cursor = loading ? 'not-allowed' : 'pointer'
    contactDisplay.style.color = loading ? 'hsl(215.4, 16.3%, 46.9%)' : ''
  }

  function contactToItem(c) {
    return {
      id: c.id,
      name: c.name || 'Unbekannt',
      email: c.email || '',
      anrede: c.anrede != null ? c.anrede : 'Herr',
      isMale: c.isMale !== false,
      isCompany: c.isCompany === true,
      label: c.label || (c.name || '') + (c.email ? ' · ' + c.email : '')
    }
  }

  function applyContactSelection(c, options) {
    var updateMessage = !options || options.updateMessage !== false
    selectedContact = c
    contactDisplay.value = c.label || (c.name + (c.email ? ' · ' + c.email : ''))
    contactClearBtn.style.display = 'block'
    contactDrop.style.display = 'none'
    emailWarning.style.display = (sendEmailMode && !c.email) ? 'block' : 'none'
    if (!updateMessage || !sendEmailMode) return
    var displayName = (c.name && c.name.indexOf('@') === -1) ? c.name : 'Unbekannt'
    var newFirstLine
    var anredeSuffix, anrede
    if (c.isCompany) {
      newFirstLine = 'Sehr geehrte Damen und Herren,'
      anredeSuffix = ''
      anrede = 'Damen und Herren'
    } else {
      anrede = c.anrede || 'Herr'
      anredeSuffix = (c.isMale !== false) ? 'r' : ''
      newFirstLine = 'Sehr geehrte' + anredeSuffix + ' ' + anrede + ' ' + displayName + ','
    }
    if (defaultBodyWithCompany && messageTextarea.value === defaultBodyWithCompany) {
      var body = defaultBodyWithCompany
        .replace(/\{\{anredeSuffix\}\}/g, anredeSuffix)
        .replace(/\{\{anrede\}\}/g, anrede)
        .replace(/\{\{name\}\}/g, displayName)
      messageTextarea.value = body
    } else {
      var lines = messageTextarea.value.split('\n')
      if (lines[0].indexOf('Sehr geehrte') === 0) {
        lines[0] = newFirstLine
        messageTextarea.value = lines.join('\n')
      }
    }
  }

  function renderContactOptions(items) {
    contactOpts.innerHTML = ''
    if (!items || items.length === 0) {
      var empty = document.createElement('div')
      empty.style.cssText = 'padding: 12px; text-align: center; color: hsl(215.4, 16.3%, 46.9%); font-size: 14px; font-family: ' + DIALOG_FONT_FAMILY + ';'
      empty.textContent = 'Keine Kontakte gefunden'
      contactOpts.appendChild(empty)
      return
    }
    items.forEach(function (c) {
      var item = document.createElement('div')
      item.style.cssText = 'padding: 8px 12px; cursor: pointer; font-size: 14px; font-weight: 500; font-family: ' + DIALOG_FONT_FAMILY + ';'
      item.textContent = c.label || c.name
      var sub = document.createElement('div')
      sub.style.cssText = 'font-size: 12px; color: hsl(215.4, 16.3%, 46.9%); font-weight: normal; margin-top: 2px; font-family: ' + DIALOG_FONT_FAMILY + ';'
      sub.textContent = c.email ? 'E-Mail: ' + c.email : 'Keine E-Mail'
      item.appendChild(sub)
      item.addEventListener('mouseover', function () { item.style.background = 'hsl(210, 40%, 96.1%)' })
      item.addEventListener('mouseout', function () { item.style.background = 'hsl(0, 0%, 100%)' })
      item.addEventListener('click', function () { applyContactSelection(c, { updateMessage: true }) })
      contactOpts.appendChild(item)
    })
  }

  function preselectApproverContact() {
    if (approverContactFromApi) {
      var byId = contacts.find(function (c) { return String(c.id) === String(approverContactFromApi.id) })
      if (byId) {
        applyContactSelection(byId, { updateMessage: false })
        return
      }
      var fromApi = contactToItem(approverContactFromApi)
      contacts = [fromApi].concat(contacts)
      renderContactOptions(contacts)
      applyContactSelection(fromApi, { updateMessage: false })
      return
    }
    if (!approvalData || !approvalData.approverEmail) return
    var email = (approvalData.approverEmail || '').trim().toLowerCase()
    if (!email) return
    var found = contacts.find(function (c) { return (c.email || '').trim().toLowerCase() === email })
    if (found) {
      applyContactSelection(found, { updateMessage: false })
      return
    }
    var synthetic = contactToItem({ id: approvalData.approvingUser || email, name: 'Unbekannt', email: email, label: email, anrede: 'Herr', isMale: true })
    contacts = [synthetic].concat(contacts)
    renderContactOptions(contacts)
    applyContactSelection(synthetic, { updateMessage: false })
  }

  function loadContactSearch(query) {
    var authToken = null
    try { authToken = JSON.parse(localStorage.auth).token } catch (e) { return }
    if (!query || query.length < 2) {
      renderContactOptions(contacts)
      return
    }
    contactSpinner.style.display = 'block'
    contactOpts.style.display = 'none'
    var origin = window.location.origin
    var searchUrl = origin + '/services/pmp-accounting/api/v1/contacts/search?name=' + encodeURIComponent(query) + '&types=PERSON,COMPANY,MANAGEMENT_COMPANY&size=50'
    fetch(searchUrl, {
      headers: { 'Authorization': 'Bearer ' + authToken, 'Content-Type': 'application/json' }
    })
      .then(function (r) { return r.ok ? r.json() : { content: [] } })
      .then(function (data) {
        contactSpinner.style.display = 'none'
        contactOpts.style.display = 'block'
        var list = (data.content || []).map(function (contact) {
          var email = contact.email
          if (email != null && typeof email === 'object' && !Array.isArray(email)) email = email.value
          else if (Array.isArray(email)) email = email[0]
          if (email != null && typeof email === 'object' && email.value) email = email.value
          else if (typeof email !== 'string') email = ''
          email = email || ''
          var info = contactNameAndSalutation(contact)
          return contactToItem({ id: contact.id, name: info.name, email: email, anrede: info.anrede, isMale: info.isMale, isCompany: info.isCompany, label: info.name + (email ? ' · ' + email : '') })
        })
        renderContactOptions(list)
      })
      .catch(function () {
        contactSpinner.style.display = 'none'
        contactOpts.style.display = 'block'
        renderContactOptions(contacts)
      })
  }

  function onDocumentClick(e) {
    if (!contactRoot.isConnected) {
      document.removeEventListener('click', onDocumentClick)
      return
    }
    if (!contactRoot.contains(e.target)) {
      contactDrop.style.display = 'none'
      contactJustOpenedByFocus = false
      if (selectedContact) {
        contactDisplay.value = selectedContact.label || (selectedContact.name + (selectedContact.email ? ' · ' + selectedContact.email : ''))
      }
    }
  }

  var contactJustOpenedByFocus = false
  contactDisplay.addEventListener('click', function (e) {
    e.stopPropagation()
    if (contactDrop.style.display === 'none' || contactDrop.style.display === '') {
      // Defer open so focus handler doesn't run first and get toggled closed by this click
      setTimeout(function () {
        contactDrop.style.display = 'block'
        var q = (contactDisplay.value || '').trim()
        var isSelectedDisplay = selectedContact && (contactDisplay.value === (selectedContact.label || (selectedContact.name + (selectedContact.email ? ' · ' + selectedContact.email : ''))))
        if (isSelectedDisplay || q.length < 2) {
          renderContactOptions(contacts)
        } else {
          loadContactSearch(q)
        }
      }, 0)
    } else {
      if (contactJustOpenedByFocus) {
        contactJustOpenedByFocus = false
      } else {
        contactDrop.style.display = 'none'
      }
    }
  })
  contactDisplay.addEventListener('input', function () {
    var q = (this.value || '').trim()
    if (!q) contactClearBtn.style.display = 'none'
    else contactClearBtn.style.display = 'block'
    if (contactSearchTimeout) clearTimeout(contactSearchTimeout)
    if (q.length >= 2) {
      contactSearchTimeout = setTimeout(function () { loadContactSearch(q) }, 300)
    } else {
      contactDrop.style.display = 'block'
      renderContactOptions(contacts)
    }
  })
  contactDisplay.addEventListener('focus', function () {
    contactDrop.style.display = 'block'
    contactJustOpenedByFocus = true
    var q = (contactDisplay.value || '').trim()
    var isSelectedDisplay = selectedContact && (contactDisplay.value === (selectedContact.label || (selectedContact.name + (selectedContact.email ? ' · ' + selectedContact.email : ''))))
    if (isSelectedDisplay || q.length < 2) {
      renderContactOptions(contacts)
    } else {
      loadContactSearch(q)
    }
  })
  document.addEventListener('click', onDocumentClick)

  contactRoot.appendChild(contactDisplay)
  contactRoot.appendChild(contactClearBtn)
  contactRoot.appendChild(contactDrop)
  body.appendChild(contactLabel)
  body.appendChild(contactRoot)

  var currentInvoiceId = (window.location.pathname.split('/').pop() || '').trim()
  if (window.invoiceSectionContextInvoiceId && window.invoiceSectionContextInvoiceId !== currentInvoiceId) {
    window.invoiceSectionPropertyId = null
    window.invoiceSectionPropertyContext = null
  }
  var propertyContext = window.invoiceSectionPropertyContext || null
  var hasMatchingContext = !!(propertyContext && propertyContext.invoiceId === currentInvoiceId)
  var propertyId = hasMatchingContext ? propertyContext.propertyId : (typeof window.invoiceSectionPropertyId !== 'undefined' ? window.invoiceSectionPropertyId : null)
  var propertyHrId = hasMatchingContext ? propertyContext.propertyHrId : null
  function extractPropertyTypeFromHrId(hrId) {
    if (!hrId || typeof hrId !== 'string') return null
    if (hrId.indexOf('WEG') === 0) return 'WEG'
    if (hrId.indexOf('SEV') === 0) return 'SEV'
    if (hrId.indexOf('MV') === 0) return 'MV'
    return null
  }
  var propertyType = extractPropertyTypeFromHrId(propertyHrId)
  if (propertyId != null) {
    var authToken = null
    try { authToken = JSON.parse(localStorage.auth).token } catch (e) {}
    if (authToken) {
      contactSpinner.style.display = 'block'
      contactOpts.style.display = 'none'
      setContactInputLoading(true)
      var baseUrl = window.location.origin
      var contractsUrl = baseUrl + '/services/pmp-accounting/api/v1/contracts?propertyId=' + encodeURIComponent(propertyId) + '&type=PROPERTY_OWNER'
      var unitContractUrl = baseUrl + '/services/pmp-accounting/api/v1/unit-contract?propertyId=' + encodeURIComponent(propertyId)
      var headers = { 'Authorization': 'Bearer ' + authToken, 'Content-Type': 'application/json' }
      Promise.all([
        fetch(contractsUrl, { headers: headers }).then(function (r) { return r.ok ? r.json() : [] }),
        fetch(unitContractUrl, { headers: headers }).then(function (r) { return r.ok ? r.json() : [] })
      ]).then(function (results) {
        var contractsData = Array.isArray(results[0]) ? results[0] : []
        var unitContracts = Array.isArray(results[1]) ? results[1] : []
        var today = new Date()
        today.setHours(0, 0, 0, 0)

        function parseDate(value) {
          if (!value) return null
          var d = new Date(value)
          return isNaN(d.getTime()) ? null : d
        }

        function roleIncludes(roles, role) {
          return Array.isArray(roles) ? roles.indexOf(role) !== -1 : (roles && String(roles).indexOf(role) !== -1)
        }

        function isContractValid(contract) {
          if (!contract) return false
          var signedDate = parseDate(contract.signedDate)
          var endDate = parseDate(contract.endDate)
          if (signedDate && signedDate > today) return false
          if (endDate && endDate < today) return false
          return true
        }

        function pushUniqueRef(list, ref) {
          if (!ref || !ref.id) return
          if (!list.some(function (it) { return it.id === ref.id })) list.push(ref)
        }

        function councilLabelPrefix(roles) {
          var hasHead = roleIncludes(roles, 'HEAD_OF_COUNCIL')
          var hasCouncil = roleIncludes(roles, 'COUNCIL')
          if (hasHead && !hasCouncil) return 'Beiratsvorsitz'
          if (hasCouncil && !hasHead) return 'Beirat'
          if (hasHead && hasCouncil) return 'Beirat, Beiratsvorsitz'
          return 'Beirat'
        }

        var mvOwnerRefs = []
        var sevOwnerRefs = []
        var councilRefs = []

        contractsData.forEach(function (contract) {
          if (!isContractValid(contract) || !Array.isArray(contract.contacts)) return
          contract.contacts.forEach(function (c) {
            if (!c || !c.contactId) return
            var isPropertyOwner = roleIncludes(c.role, 'PROPERTY_OWNER') || roleIncludes(c.role, 'OWNER')
            if (isPropertyOwner || !c.role) {
              pushUniqueRef(mvOwnerRefs, { id: c.contactId, type: 'MV', name: c.name, labelPrefix: 'Objekteigentümer' })
            }
          })
        })

        unitContracts.forEach(function (contract) {
          if (!isContractValid(contract) || !Array.isArray(contract.contacts)) return
          contract.contacts.forEach(function (c) {
            if (!c || !c.contactId) return
            if (roleIncludes(c.role, 'OWNER')) {
              pushUniqueRef(sevOwnerRefs, { id: c.contactId, type: 'SEV', name: c.name, labelPrefix: 'Eigentümer' })
            }
            if (roleIncludes(c.role, 'COUNCIL') || roleIncludes(c.role, 'HEAD_OF_COUNCIL')) {
              pushUniqueRef(councilRefs, { id: c.contactId, type: 'WEG', name: c.name, labelPrefix: councilLabelPrefix(c.role) })
            }
          })
        })

        // Filter suggestions by property type.
        var contactIds = []
        var baseSuggestions
        if (propertyType === 'WEG') baseSuggestions = councilRefs
        else if (propertyType === 'SEV') baseSuggestions = sevOwnerRefs
        else if (propertyType === 'MV') baseSuggestions = mvOwnerRefs
        else baseSuggestions = sevOwnerRefs.concat(mvOwnerRefs).concat(councilRefs)

        baseSuggestions.forEach(function (ref) {
          pushUniqueRef(contactIds, ref)
        })
        if (contactIds.length === 0) {
          contactSpinner.style.display = 'none'
          contactOpts.style.display = 'block'
          renderContactOptions([])
          preselectApproverContact()
          setContactInputLoading(false)
          return
        }
        Promise.all(contactIds.map(function (ref) {
          return fetch(baseUrl + '/services/pmp-accounting/api/v1/contacts/' + ref.id, { headers: headers })
            .then(function (r) { return r.ok ? r.json() : null })
            .then(function (contact) {
              var name = ref.name
              var email = ''
              var anrede = 'Herr'
              var isMale = true
              var isCompany = false
              if (contact) {
                var info = contactNameAndSalutation(contact)
                name = info.name
                anrede = info.anrede
                isMale = info.isMale
                isCompany = info.isCompany
                email = (contact.email != null) ? (typeof contact.email === 'string' ? contact.email : (contact.email[0] || contact.email)) : ''
                if (email && typeof email === 'object' && email.value) email = email.value
                else if (typeof email !== 'string') email = ''
              }
              if (!name) name = 'Unbekannt'
              var labelPrefix = ref.labelPrefix != null ? ref.labelPrefix : ref.type
              return contactToItem({ id: ref.id, name: name, email: email || '', anrede: anrede, isMale: isMale, isCompany: isCompany, label: labelPrefix + ': ' + name })
            })
        })).then(function (loaded) {
          contacts = loaded.filter(function (c) { return !!c })
          contactSpinner.style.display = 'none'
          contactOpts.style.display = 'block'
          renderContactOptions(contacts)
          preselectApproverContact()
          if (!selectedContact && (!approvalData || !approvalData.approverEmail) && contacts.length > 0) {
            var firstWithEmail = contacts.find(function (c) { return !!((c.email || '').trim()) })
            if (firstWithEmail) {
              applyContactSelection(firstWithEmail, { updateMessage: true })
            }
          }
          setContactInputLoading(false)
        }).catch(function () {
          contactSpinner.style.display = 'none'
          contactOpts.style.display = 'block'
          renderContactOptions([])
          preselectApproverContact()
          setContactInputLoading(false)
        })
      }).catch(function () {
        contactSpinner.style.display = 'none'
        contactOpts.style.display = 'block'
        renderContactOptions([])
        preselectApproverContact()
        setContactInputLoading(false)
      })
    } else {
      renderContactOptions([])
      preselectApproverContact()
      setContactInputLoading(false)
    }
  } else {
    renderContactOptions([])
    preselectApproverContact()
  }


  const emailWarning = document.createElement('div')
  emailWarning.style.cssText = `
    font-size: 13px;
    color: hsl(0, 84.2%, 60.2%);
    display: none;
    font-family: ${DIALOG_FONT_FAMILY};
  `
  emailWarning.textContent = 'Ohne E-Mail-Adresse kann die Freigabe nicht angefordert werden. Bitte wählen Sie einen Kontakt mit E-Mail.'

  body.appendChild(emailWarning)

  function makeSendModeRadio(value, labelText, checked) {
    var wrap = document.createElement('label')
    wrap.style.cssText = 'display:flex;align-items:center;gap:8px;font-size:14px;cursor:pointer;font-family:' + DIALOG_FONT_FAMILY + ';'
    var input = document.createElement('input')
    input.type = 'radio'
    input.name = 'capex-send-mode'
    input.value = value
    input.checked = !!checked
    input.addEventListener('change', function () {
      if (!input.checked) return
      sendEmailMode = value === 'email'
      messageTextarea.disabled = !sendEmailMode
      messageTextarea.style.opacity = sendEmailMode ? '1' : '0.55'
      messageTextarea.style.backgroundColor = sendEmailMode ? '' : '#f5f5f5'
      messageTextarea.style.cursor = sendEmailMode ? 'text' : 'not-allowed'
      emailWarning.style.display = (sendEmailMode && selectedContact && !selectedContact.email) ? 'block' : 'none'
      updateSendBtnLabel()
    })
    wrap.appendChild(input)
    wrap.appendChild(document.createTextNode(labelText))
    return wrap
  }

  body.appendChild(makeSendModeRadio('email', 'E-Mail senden', true))
  body.appendChild(makeSendModeRadio('silent', 'Keine E-Mail senden', false))

  // Message (preview and edit)
  const messageLabel = document.createElement('label')
  messageLabel.style.cssText = `font-size: 14px; font-weight: 500; color: hsl(222.2, 84%, 4.9%); display: block; margin-bottom: 6px; font-family: ${DIALOG_FONT_FAMILY};`
  messageLabel.textContent = 'Nachricht (Vorschau und Bearbeitung)'

  const messageTextarea = document.createElement('textarea')
  messageTextarea.value = (approvalData && approvalData.emailContent) ? approvalData.emailContent : DEFAULT_EMAIL_BODY
  messageTextarea.rows = 8
  messageTextarea.style.cssText = `
    width: 100%;
    padding: 10px 12px;
    font-size: 14px;
    border: 1px solid hsl(214.3, 31.8%, 91.4%);
    border-radius: 4px;
    resize: vertical;
    box-sizing: border-box;
    font-family: ${DIALOG_FONT_FAMILY};
  `

  body.appendChild(messageLabel)
  body.appendChild(messageTextarea)

  // When no existing approval (or empty content), fetch current domain legal name and replace placeholder in default message
  if (!approvalData || !approvalData.emailContent) {
    var authToken = null
    try { authToken = JSON.parse(localStorage.auth).token } catch (e) {}
    if (authToken) {
      fetch(baseApiUrl + '/api/domain/company-name', {
        headers: { 'Authorization': 'Bearer ' + authToken }
      })
        .then(function (r) { return r.ok ? r.json() : null })
        .then(function (data) {
          if (data && data.legalName != null) {
            defaultBodyWithCompany = getDefaultBodyWithCompanyName(data.legalName)
            messageTextarea.value = defaultBodyWithCompany
            if (selectedContact) {
              applyContactSelection(selectedContact, { updateMessage: true })
            }
          }
        })
        .catch(function () {})
    }
  }

  const footer = document.createElement('div')
  footer.style.cssText = `
    padding: 12px 20px;
    border-top: 1px solid hsl(214.3, 31.8%, 91.4%);
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    font-family: ${DIALOG_FONT_FAMILY};
  `

  const cancelBtn = document.createElement('button')
  cancelBtn.textContent = 'Abbrechen'
  cancelBtn.style.cssText = `
    padding: 8px 16px;
    height: 40px;
    font-size: 14px;
    font-weight: 500;
    border: 1px solid hsl(214.3, 31.8%, 91.4%);
    border-radius: 2px;
    background: hsl(0, 0%, 100%);
    color: hsl(222.2, 47.4%, 11.2%);
    cursor: pointer;
    transition: background-color 0.2s, color 0.2s;
    font-family: ${DIALOG_FONT_FAMILY};
  `
  cancelBtn.onclick = close
  cancelBtn.addEventListener('mouseover', function () {
    cancelBtn.style.backgroundColor = 'hsl(210, 40%, 96.1%)'
    cancelBtn.style.color = 'hsl(222.2, 47.4%, 11.2%)'
  })
  cancelBtn.addEventListener('mouseout', function () {
    cancelBtn.style.backgroundColor = 'hsl(0, 0%, 100%)'
    cancelBtn.style.color = 'hsl(222.2, 47.4%, 11.2%)'
  })

  function updateSendBtnLabel() {
    if (!sendEmailMode) sendBtn.textContent = 'Freigabe anfragen'
    else if (approvalData) sendBtn.textContent = 'E-Mail erneut senden'
    else sendBtn.textContent = 'E-Mail senden'
  }

  const sendBtn = document.createElement('button')
  updateSendBtnLabel()
  sendBtn.style.cssText = `
    padding: 8px 16px;
    height: 40px;
    font-size: 14px;
    font-weight: 500;
    border: none;
    border-radius: 2px;
    background: hsl(222.2, 47.4%, 11.2%);
    color: hsl(210, 40%, 98%);
    cursor: pointer;
    transition: background-color 0.2s, opacity 0.2s;
    font-family: ${DIALOG_FONT_FAMILY};
  `
  sendBtn.addEventListener('mouseover', function () {
    if (!sendBtn.disabled) sendBtn.style.backgroundColor = 'rgba(0, 0, 0, 0.75)'
  })
  sendBtn.addEventListener('mouseout', function () {
    sendBtn.style.backgroundColor = 'hsl(222.2, 47.4%, 11.2%)'
  })
  sendBtn.onclick = function () {
    if (!selectedContact) {
      contactDisplay.focus()
      return
    }
    if (sendEmailMode && !selectedContact.email) {
      emailWarning.style.display = 'block'
      return
    }
    if(sendBtn.disabled) return
    var authToken = null
    try { authToken = JSON.parse(localStorage.auth).token } catch (e) {}
    if (!authToken) return
    emailWarning.style.display = 'none'
    sendBtn.disabled = true
    sendBtn.style.cursor = 'not-allowed'
    sendBtn.style.opacity = '0.5'
    sendBtn.textContent = sendEmailMode ? 'E-Mail wird gesendet...' : 'Freigabe wird angefragt...'
    const invoiceId = window.location.pathname.split('/').pop()
    const apiUrl = baseApiUrl + (
      approvalData 
      ? `/api/invoices/${invoiceId}/capex-request-approval/re-send` 
      : `/api/invoices/${invoiceId}/capex-request-approval`)
    fetch(apiUrl, {
      method: 'POST',
      body: JSON.stringify({
        email: selectedContact.email || undefined,
        contactId: selectedContact.id,
        textContent: sendEmailMode ? messageTextarea.value : '',
        sendEmail: sendEmailMode,
      }),
      headers: { 'Authorization': 'Bearer ' + authToken, 'Content-Type': 'application/json' }
    }).then(async function (r) {
        if (!r.ok) {
          alert('Failed to send external approval email')
          return
        }
        const data = await r.json()
        approvalData = data.capexApproval
        approvalBadgeLabel.textContent = openApprovalMetaText(approvalData)
        approvalBadgeLabel.style.display = 'inline'
        if (titleLabel && baseTitle) titleLabel.textContent = baseTitle
        if (approverLabelApi && selectedContact) {
          approverLabelApi.setApproverName(selectedContact.name || selectedContact.label)
          if (approverLabelApi.showApprover) approverLabelApi.showApprover()
        }
        close()
      }).catch(function (error) {
      console.error(error);
      sendBtn.disabled = false
      sendBtn.style.cursor = 'pointer'
      sendBtn.style.opacity = '1'
      updateSendBtnLabel()
      alert('Failed to send external approval email')
    })
  }

  footer.appendChild(cancelBtn)
  footer.appendChild(sendBtn)

  dialog.appendChild(header)
  dialog.appendChild(body)
  dialog.appendChild(footer)
  overlay.appendChild(dialog)

  function close() {
    document.removeEventListener('click', onDocumentClick)
    overlay.remove()
    document.body.style.overflow = ''
  }

  var overlayMouseDownOnOverlay = false
  overlay.addEventListener('mousedown', function (e) {
    overlayMouseDownOnOverlay = (e.target === overlay)
  })
  overlay.addEventListener('click', function (e) {
    if (e.target === overlay && overlayMouseDownOnOverlay) close()
    overlayMouseDownOnOverlay = false
  })
  document.body.style.overflow = 'hidden'
  document.body.appendChild(overlay)
}

/**
 * Injects external approval UI into the invoice page.
 * @param {HTMLElement} approvalSection - The #approval-section container (approval cards + button row)
 * @param {HTMLElement} sectionTitle - The section title element where the badge is placed (e.g. next to "Allgemeines")
 * @returns {{ externalApprovalCardContainer: HTMLElement, externalApprovalBadge: HTMLElement, externalApprovalButtonRow: HTMLElement }}
 */
function externalApprovalForInvoice(approvalSection, sectionTitle) {
  if (!approvalSection || !sectionTitle) return {}

  // 1) External approval card (third switch) – always disabled/greyed for employees
  const externalApprovalBadge = document.createElement('span')
  externalApprovalBadge.id = 'external-approval-badge'
  externalApprovalBadge.textContent = 'Externe Freigabe erforderlich'
  externalApprovalBadge.style.cssText = `
    display: none;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    margin-left: 8px;
    background-color: #fef3c7;
    color: #b45309;
  `
  var badgeContainer = sectionTitle.querySelector('#section-header-badges')
  if (badgeContainer) {
    badgeContainer.insertBefore(externalApprovalBadge, badgeContainer.firstChild)
  } else {
    externalApprovalBadge.style.marginLeft = '8px'
    sectionTitle.appendChild(externalApprovalBadge)
  }
  function setBadgeStatus(status) {
    if (!externalApprovalBadge) return
    if (status === 'approved') {
      externalApprovalBadge.textContent = 'Externe Freigabe'
      externalApprovalBadge.style.backgroundColor = '#dcfce7'
      externalApprovalBadge.style.color = '#166534'
    } else if (status === 'skipped') {
      externalApprovalBadge.textContent = 'Freigabe übersprungen'
      externalApprovalBadge.style.backgroundColor = '#fef9c3'
      externalApprovalBadge.style.color = '#a16207'
    } else if (status === 'rejected') {
      externalApprovalBadge.textContent = 'Durch extern abgelehnt'
      externalApprovalBadge.style.backgroundColor = '#fee2e2'
      externalApprovalBadge.style.color = '#b91c1c'
    } else {
      externalApprovalBadge.textContent = 'Externe Freigabe erforderlich'
      externalApprovalBadge.style.backgroundColor = '#fef3c7'
      externalApprovalBadge.style.color = '#b45309'
    }
  }

  var skipAvailability = { allow: false, skipped: false }

  var externalApprovalRow = document.createElement('div')
  externalApprovalRow.className = 'approval-card-row'
  externalApprovalRow.dataset.approvalId = 'freigegebenExterneFreigabeRow'
  externalApprovalRow.style.cssText =
    'display:none;position:relative;width:100%;box-sizing:border-box;'

  var externalApprovalActionsMenu = createLuxApprovalKebabMenu([
    {
      id: 'skip',
      label: 'Überspringen',
      onClick: function () {
        buildSkipExternalApprovalDialog(function (result) {
          skipAvailability.skipped = true
          refreshSkipMenuVisibility()
          applyExternalApprovalSkippedVisual(externalApproval, setBadgeStatus)
          if (typeof window.onExternalApprovalSkipped === 'function') {
            window.onExternalApprovalSkipped(result)
          }
        })
      },
    },
  ])
  externalApprovalActionsMenu.container.style.position = 'absolute'
  externalApprovalActionsMenu.container.style.left = '100%'
  externalApprovalActionsMenu.container.style.zIndex = '1'
  externalApprovalActionsMenu.container.style.top = '50%'
  externalApprovalActionsMenu.container.style.transform = 'translateY(-50%)'
  externalApprovalActionsMenu.container.style.marginLeft = '8px'

  function refreshSkipMenuVisibility() {
    if (skipAvailability.skipped) {
      externalApprovalActionsMenu.hide()
      return
    }
    externalApprovalActionsMenu.setItemVisible(
      'skip',
      skipAvailability.allow && externalApproval && !externalApproval.checkbox.checked
    )
    externalApprovalActionsMenu.refreshMenuVisible()
  }

  const externalApproval = createApprovalCard(
    'freigegebenExterneFreigabe',
    'Externe Freigabe',
    true,
    openExternalApprovalDialog,
    setBadgeStatus,
    {
      shouldSuppressRequestActions: function () {
        return skipAvailability.skipped
      },
      onControlsRefresh: refreshSkipMenuVisibility,
    }
  )
  externalApproval.actionsMenu = externalApprovalActionsMenu
  externalApproval.refreshSkipMenu = refreshSkipMenuVisibility
  refreshSkipMenuVisibility()

  externalApproval.card.style.width = '100%'
  externalApproval.card.style.boxSizing = 'border-box'
  externalApprovalRow.appendChild(externalApproval.card)
  externalApprovalRow.appendChild(externalApprovalActionsMenu.container)
  approvalSection.appendChild(externalApprovalRow)

  return {
    externalApprovalCardContainer: externalApprovalRow,
    externalApprovalBadge,
    // externalApprovalButtonRow: buttonRow,
    setSkipApprovalAvailability: function (allowSkip) {
      skipAvailability.allow = !!allowSkip
      refreshSkipMenuVisibility()
    },
    clearPersistedSkipOverride: function () {
      skipAvailability.skipped = false
      refreshSkipMenuVisibility()
      restoreExternalApprovalRequestUi(externalApproval, setBadgeStatus)
    },
    setExternalApprovalSkipped: function () {
      skipAvailability.skipped = true
      refreshSkipMenuVisibility()
      applyExternalApprovalSkippedVisual(externalApproval, setBadgeStatus)
    },
    setExternalApprovalChecked: function (checked, metaText, approverDisplay, approverContactId, changedAt) {
      externalApproval.checkbox.checked = !!checked
      var formattedChangedAt = formatApprovalDateTime(changedAt)
      if (checked) {
        externalApproval.slider.style.backgroundColor = '#22c55e'
        externalApproval.sliderDot.style.transform = 'translateX(22px)'
        if (metaText) {
          externalApproval.metaLabel.textContent = '— ' + metaText
          externalApproval.metaLabel.style.display = 'inline'
        } else if (formattedChangedAt) {
          externalApproval.metaLabel.textContent = '— freigegeben · ' + formattedChangedAt
          externalApproval.metaLabel.style.display = 'inline'
        } else {
          externalApproval.metaLabel.style.display = 'none'
        }
        if (approverContactId && externalApproval.loadApproverName) {
          externalApproval.loadApproverName(approverContactId)
        } else if (approverDisplay && externalApproval.setApproverName) {
          var display = (typeof approverDisplay === 'string' && approverDisplay.indexOf('@') !== -1) ? '' : approverDisplay
          externalApproval.setApproverName(display || 'Unbekannt')
        }
      } else {
        externalApproval.slider.style.backgroundColor = '#ccc'
        externalApproval.sliderDot.style.transform = 'translateX(0)'
        if (metaText) {
          externalApproval.metaLabel.textContent = '— ' + metaText
          externalApproval.metaLabel.style.display = 'inline'
        } else if (formattedChangedAt) {
          externalApproval.metaLabel.textContent = '— abgelehnt · ' + formattedChangedAt
          externalApproval.metaLabel.style.display = 'inline'
        } else {
          externalApproval.metaLabel.style.display = 'none'
        }
        if (externalApproval.hideApprover) externalApproval.hideApprover()
      }
    }
  }
}
